import React from 'react';

import useAcl from '@/hooks/user/useAcl';
import { DuplicateDashboardProvider } from '@/utils/hooks/dashboard/useDuplicateDashboard';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Utils
import { put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { UPDATE_DASHBOARD } from '@/lib/routes/apiEndpoints';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import { DeleteConfirmModal } from '@/components/pages/custom-dashboards/modals/DeleteConfirmModal';
import DuplicateModals from '@/components/pages/custom-dashboards/modals/DuplicateModals';
import { EditNameModal } from '@/components/pages/custom-dashboards/modals/EditNameModal';
import ExportModal from '@/components/pages/custom-dashboards/modals/ExportModal';
import { ShareModals } from '@/components/pages/custom-dashboards/modals/ShareModals';

const ActionButton = ({ icon, tooltip, onClick }) => {
    const { t } = useTranslation('dashboard');
    const handleChange = (e) => {
        e && e.preventDefault();
        onClick();
    };

    return (
        <Tooltip position="top" content={t(tooltip)}>
            <Icon icon={icon} onClick={handleChange} />
        </Tooltip>
    );
};

const Delete = ({ dashboard, mutateDashboards }) => {
    const { isOpen, toggle } = useToggle();

    return (
        <>
            <DeleteConfirmModal
                isOpen={isOpen}
                toggle={toggle}
                dashboard={dashboard}
                onSuccess={mutateDashboards}
                successBanner={false}
            />
            <ActionButton icon="trash" tooltip="delete_dashboard" onClick={toggle} />
        </>
    );
};

const Share = ({ dashboard }) => {
    const { isOpen, toggle } = useToggle();

    return (
        <>
            <ShareModals isOpen={isOpen} toggle={toggle} dashboard={dashboard} />
            <ActionButton icon="share" tooltip="share_dashboard" onClick={toggle} />
        </>
    );
};

const Duplicate = ({ dashboard, mutateDashboards }) => {
    const { isOpen, toggle } = useToggle();

    return (
        <>
            <DuplicateModals
                dashboard={dashboard}
                isOpen={isOpen}
                toggle={toggle}
                onSuccess={mutateDashboards}
            />
            <ActionButton icon="duplicate" tooltip="duplicate_dashboard" onClick={toggle} />
        </>
    );
};

const Export = ({ dashboard }) => {
    const { hasAcl } = useAcl();
    const isLocked = !hasAcl(aclFeatures.report.create.full);
    const { isOpen, toggle } = useToggle();

    return (
        <>
            <ExportModal
                isLocked={isLocked}
                isOpen={isOpen}
                toggle={toggle}
                dashboard={dashboard}
            />
            <ActionButton icon="export" tooltip="download_dashboard" onClick={toggle} />
        </>
    );
};

const Edit = ({ mutateDashboards, dashboard }) => {
    const { t } = useTranslation('dashboard');

    const { isOpen, toggle } = useToggle();

    const onConfirm = async (name) => {
        if (name !== dashboard.name) {
            await put(
                injectEndpointUrlParams(UPDATE_DASHBOARD, {
                    dashboardId: dashboard.id
                }),
                {
                    name
                }
            );
            await mutateDashboards();
            toggle();
        } else {
            toggle();
        }
    };

    return (
        <>
            <EditNameModal
                isOpen={isOpen}
                toggle={toggle}
                isEditing
                onConfirm={onConfirm}
                title={t('edit_name_dashboard')}
                btnLabel={t('dashboard_confirm_btn_label')}
                dashboard={dashboard}
            />
            <ActionButton icon="edit" tooltip="edit_name_dashboard" onClick={toggle} />
        </>
    );
};

const DashboardActionsModal = ({ dashboard, mutateDashboards }) => {
    return (
        <div className="absolute right-4 top-4 flex cursor-pointer gap-4 text-icoGray-500 sm:opacity-0 sm:group-hover:opacity-100">
            <DuplicateDashboardProvider
                defaultValues={{
                    name: dashboard.name,
                    social_profiles: []
                }}>
                <Edit mutateDashboards={mutateDashboards} dashboard={dashboard} />
                {dashboard?.data?.social_profiles?.length ? (
                    <Duplicate dashboard={dashboard} mutateDashboards={mutateDashboards} />
                ) : null}
                <Export dashboard={dashboard} />
                <Share dashboard={dashboard} />
                <Delete dashboard={dashboard} mutateDashboards={mutateDashboards} />
            </DuplicateDashboardProvider>
        </div>
    );
};

export default DashboardActionsModal;
